import { observer } from 'mobx-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { Illustration } from '@/components/Illustration/Illustration'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, AGE_VERIFICATION_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { cx } from '@/utils/cx'
import {
  authenticationResponseToJSON,
  isConditionalMediationAvailable,
  parseRequestOptions,
  PublicKeyCredentialRequestOptionsJSON,
} from '@/methods/webauthn'

interface CreateEidTransactionResponse {
  proceedUrl: string
}

const MAX_PASSKEY_ATTEMPTS = 3

export const AgeVerificationStartPage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const {
    ageVerificationPage: { start: trans },
  } = store.TranslationsState.translations

  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [eidBtnLoading, setEidBtnLoading] = useState(false)
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [error, setError] = useState('')

  const startEidVerification = async () => {
    try {
      const { data } = await axiosInstance.post<CreateEidTransactionResponse>(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/eid`,
        {},
        {
          withCredentials: true,
        }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (error) {
      devLog('Error creating eid transaction:', error)
      setBtnLoading(false)
      setEidBtnLoading(false)
    }
  }

  const authenticateWithPasskey = async (): Promise<boolean> => {
    try {
      const { data: requestOptions } =
        await axiosInstance.post<PublicKeyCredentialRequestOptionsJSON>(
          `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/auth/start`,
          {},
          {
            withCredentials: true,
          }
        )

      const credential = (await navigator.credentials.get({
        publicKey: parseRequestOptions(requestOptions),
      })) as PublicKeyCredential | null

      if (!credential) {
        return false
      }

      // Responds with 204 on success and throws (4xx/5xx) on verification failure
      await axiosInstance.post(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/auth/verify`,
        authenticationResponseToJSON(credential),
        {
          withCredentials: true,
        }
      )

      return true
    } catch (error) {
      devLog('Passkey authentication failed:', error)
      return false
    }
  }

  const handleProceed = async () => {
    setBtnLoading(true)
    setError('')

    if (!isPasskeyAvailable) {
      await startEidVerification()
      return
    }

    const isVerified = await authenticateWithPasskey()

    if (isVerified) {
      store.ScenarioState.nextPage()
      return
    }

    const attempts = failedAttempts + 1
    setFailedAttempts(attempts)

    if (attempts >= MAX_PASSKEY_ATTEMPTS) {
      // Out of passkey attempts - fall back to eID verification
      await startEidVerification()
      return
    }

    setError(trans.passkeyError)
    setBtnLoading(false)
  }

  const handleProceedWithoutPasskey = async () => {
    setEidBtnLoading(true)
    await startEidVerification()
  }

  useEffect(() => {
    isConditionalMediationAvailable()
      .then(setIsPasskeyAvailable)
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div className="layout__content-container-with-scroll">
        <Illustration type="age-verification" />

        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph-centered">{trans.description}</p>

        {error && (
          <div className="generic__onboarding-item">
            <div className="generic__onboarding-icon">
              <Icon type="warning" size="32px" color="var(--text-input-error-color, #941616)" />
            </div>
            <span className="generic__onboarding-single-line generic__error-message">{error}</span>
          </div>
        )}

        <div className="generic__onboarding-list">
          {isPasskeyAvailable ? (
            <>
              <div className="generic__onboarding-item">
                <div className="generic__onboarding-icon">
                  <Icon type="passkey" size="32px" />
                </div>
                <span className="generic__onboarding-single-line">{trans.passkeyOption}</span>
              </div>
              <div className="generic__onboarding-item">
                <div className="generic__onboarding-icon">
                  <Icon type="identity-card" size="32px" />
                </div>
                <span className="generic__onboarding-single-line">{trans.eidOption}</span>
              </div>
            </>
          ) : (
            <div className="generic__onboarding-item">
              <div className="generic__onboarding-icon">
                <Icon type="identity-card" size="32px" />
              </div>
              <span className="generic__onboarding-single-line">{trans.eidHint}</span>
            </div>
          )}
        </div>

        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>

      <div className="layout__content-container-bottom-fixed-centered">
        <div className="layout__vertically-centered-container">
          <Button
            onClick={handleProceed}
            tabIndex={0}
            className="button--proceed"
            ref={buttonRef}
            disabled={btnLoading || eidBtnLoading}
          >
            {btnLoading && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
              data-testId="proceed"
            >
              {isPasskeyAvailable ? trans.proceedPasskey : trans.proceedEid}
            </span>
          </Button>

          {isPasskeyAvailable && (
            <>
              <div className="generic__spacer h-15" />
              <Button
                onClick={handleProceedWithoutPasskey}
                tabIndex={0}
                className="button--secondary"
                disabled={btnLoading || eidBtnLoading}
              >
                {eidBtnLoading && <LoadingSpinner inButton />}
                <span
                  className={cx(
                    'generic__button-text',
                    eidBtnLoading && 'generic__button-text--loading'
                  )}
                  data-testId="proceed-without-passkey"
                >
                  {trans.noPasskey}
                </span>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})
